import axios from 'axios';
import { getTenantSubdomain } from '../utils/tenantUtils';

// Use relative URL /api for production, fallback to localhost for dev
const getBaseURL = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In production (served from same domain), use relative path
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return '/api';
  }
  return 'http://localhost:5000/api';
};

const API_URL = getBaseURL();

// Create axios instance with auth interceptor
const authAxios = axios.create({
  baseURL: API_URL
});

// Add token and tenant header to requests
authAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add tenant context header
    const subdomain = getTenantSubdomain();
    if (subdomain) {
      config.headers['X-Tenant-ID'] = subdomain;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle auth errors
authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const authService = {
  // Register new user
  async register(userData) {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    return response.data;
  },

  // Login user
  async login(email, password) {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    return response.data;
  },

  // Logout user
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Get current user
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Check if user is logged in
  isAuthenticated() {
    return !!localStorage.getItem('token');
  },

  // Get user role
  getUserRole() {
    const user = this.getCurrentUser();
    return user?.role || null;
  },

  // Get user team
  getUserTeam() {
    const user = this.getCurrentUser();
    return user?.team || null;
  },

  // Check if user can access red team features
  canAccessRedTeam() {
    const role = this.getUserRole();
    return ['admin', 'tenant_admin', 'purple_team', 'red_team'].includes(role);
  },

  // Check if user can access blue team features
  canAccessBlueTeam() {
    const role = this.getUserRole();
    return ['admin', 'tenant_admin', 'purple_team', 'blue_team'].includes(role);
  },

  // Check if user is super admin (cross-tenant)
  isAdmin() {
    return this.getUserRole() === 'admin';
  },

  // Check if user is tenant admin
  isTenantAdmin() {
    return this.getUserRole() === 'tenant_admin';
  },

  // Check if user is any kind of admin (super admin or tenant admin)
  isAnyAdmin() {
    const role = this.getUserRole();
    return ['admin', 'tenant_admin'].includes(role);
  },

  // Check if user is purple team (can see both)
  isPurpleTeam() {
    const role = this.getUserRole();
    return ['admin', 'tenant_admin', 'purple_team'].includes(role);
  },

  // Get user profile
  async getProfile() {
    const response = await authAxios.get('/auth/me');
    return response.data;
  },

  // Update profile
  async updateProfile(profileData) {
    const response = await authAxios.put('/auth/profile', profileData);
    if (response.data) {
      const currentUser = this.getCurrentUser();
      localStorage.setItem('user', JSON.stringify({ ...currentUser, ...response.data }));
    }
    return response.data;
  },

  // Get all users (admin only)
  async getUsers() {
    const response = await authAxios.get('/auth/users');
    return response.data;
  },

  // Get users by team
  async getUsersByTeam(team) {
    const response = await authAxios.get(`/auth/users/team/${team}`);
    return response.data;
  },

  // Update user (admin only)
  async updateUser(userId, userData) {
    const response = await authAxios.put(`/auth/users/${userId}`, userData);
    return response.data;
  },

  // Deactivate user (admin only)
  async deactivateUser(userId) {
    const response = await authAxios.delete(`/auth/users/${userId}`);
    return response.data;
  },

  // Get token
  getToken() {
    return localStorage.getItem('token');
  }
};

export { authAxios };
export default authService;
